/**
 * src/pricingtable/PlanCard2.jsx — 2026-03-26
 * ------------------------------------------------------------------
 * PURPOSE
 * ------------------------------------------------------------------
 * WordPress-aligned pricing card renderer for the new app pricing flow.
 *
 * WHY THIS FILE EXISTS
 * ------------------------------------------------------------------
 * The legacy PlanCard.jsx still uses older in-app presentation assumptions.
 * This new file renders directly from the merged pricing presentation payload
 * that already drives the WordPress pricing page.
 *
 * TARGET VISUAL HIERARCHY
 * ------------------------------------------------------------------
 * This card mirrors the current WordPress pricing card structure:
 *
 *   • plan name
 *   • subheader / kicker
 *   • best-for line
 *   • price block
 *   • CTA
 *   • feature intro
 *   • feature list with tooltips
 *
 * IMPORTANT
 * ------------------------------------------------------------------
 * Presentation content comes from the merged payload:
 *   • subheader
 *   • bestFor
 *   • featureIntro
 *   • features
 *   • pricingDisplay
 *   • featured
 *
 * Runtime behavior still depends on billing/subscription state:
 *   • current plan detection
 *   • trialing behavior
 *   • account-context button states
 *   • self-serve vs contact-sales behavior
 *
 * DESIGN GOALS
 * ------------------------------------------------------------------
 * 1. Match the current WordPress pricing cards as closely as possible.
 * 2. Keep account/runtime behavior safe.
 * 3. Keep this file self-contained for the first testable pass.
 * 4. Add logs so local testing is easy.
 */

import React, { useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";

import { planConfigs } from "plan-catalog";
import { getPlanCtaState } from "../utils/getPlanCtaState";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/700.css";

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */

function formatMoneyParts(num) {
  if (num == null || Number.isNaN(Number(num))) {
    return ["–", ""];
  }

  const fixed = Number(num).toFixed(2);
  const [major, minor] = fixed.split(".");
  return [major, minor || "00"];
}

function getDisplayedSeatPrice(interval, perSeat) {
  if (perSeat == null) return null;

  const numeric = Number(perSeat);
  if (!Number.isFinite(numeric)) return null;

  if (interval === "annual") {
    return numeric / 12;
  }

  return numeric;
}

function getPlanPriceIdForInterval(planKey, interval) {
  const cfg = planConfigs?.[planKey] || {};
  const tier = cfg?.pricing?.[interval] || {};
  return tier?.priceId || null;
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      focusable="false"
      aria-hidden="true"
      style={{
        width: 18,
        height: 18,
        display: "block",
        fill: "currentColor",
      }}
    >
      <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  );
}

/* ------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------ */
const PlanCard2 = ({
  plan,
  seatCount,
  context,
  interval,
  onPlanSelect,
  currentSubscription,
}) => {
  const isPublic = context === "public";

  const planKey = (plan?.key || "").toString().trim().toLowerCase();

  /* --------------------------------------------------------------
   * Enterprise alignment rule:
   * - PricingTable2 is now the owner of the normalized active-interval
   *   plan object.
   * - PlanCard2 should TRUST the billing fields already prepared there
   *   first, and only fall back to legacy/raw sources if needed.
   *
   * Why:
   * - SubscriptionPage2 stores the selected plan object emitted here.
   * - ReviewChangesDrawer2 later consumes that selected plan object for:
   *     • previewBillingChange()
   *     • switchIntervalAndPay()
   *     • restart / downgrade handling
   * - Re-deriving billing fields here from separate sources creates
   *   avoidable drift risk.
   * ------------------------------------------------------------ */
  const resolvedPerSeat =
    Number.isFinite(Number(plan?.perSeat)) ?
      Number(plan.perSeat) :
      (
        Number.isFinite(Number(plan?.pricing?.[interval]?.perSeat)) ?
          Number(plan.pricing[interval].perSeat) :
          null
      );

  const displayedSeatPrice =
    plan?.displayedSeatPrice != null ?
      Number(plan.displayedSeatPrice) :
      getDisplayedSeatPrice(interval, resolvedPerSeat);

  const [major, minor] = formatMoneyParts(displayedSeatPrice);

  const isContact =
    plan?.pricingDisplay === "contact" ||
    plan?.availability?.selfServe === false ||
    plan?.cta?.type === "contact_sales";

  const priceMetaText = isContact ? "Contact sales" : "per user / month";

  const resolvedPriceId = useMemo(() => {
    if (plan?.priceId) {
      return plan.priceId;
    }

    if (!planKey) return null;
    return getPlanPriceIdForInterval(planKey, interval);
  }, [plan?.priceId, planKey, interval]);

  const currentSubscriptionPriceId = currentSubscription?.priceId || null;
  const isCurrentPlan =
    !!resolvedPriceId &&
    !!currentSubscriptionPriceId &&
    resolvedPriceId === currentSubscriptionPriceId;

  const isTrialing = currentSubscription?.status === "trialing";
  const isCanceled = currentSubscription?.status === "canceled";
  const isExpiredTrial = currentSubscription?.status === "incomplete_expired";
  const isRestart = isCanceled || isExpiredTrial;

  const isEffectiveCurrent = isCurrentPlan && !isRestart;

  const currentSeatCount = Number(seatCount || currentSubscription?.seats || 1);
  const isSeatCountUpdated =
    Number.isFinite(Number(currentSubscription?.seats)) &&
    Number(currentSeatCount) !== Number(currentSubscription?.seats);

  /**
   * Total billing amount for the currently selected user count.
   *
   * IMPORTANT:
   * - The big hero number remains PER USER / MONTH to match WordPress.
   * - This smaller supporting block shows the account total for the selected
   *   number of users.
   * - For annual pricing, we show the actual billed yearly total.
   * - Contact-sales plans do not render totals.
   */
  const totalPrice = useMemo(() => {
    if (isContact) return null;

    const perSeat =
      Number.isFinite(Number(plan?.perSeat)) ?
        Number(plan.perSeat) :
        Number(plan?.pricing?.[interval]?.perSeat ?? null);

    const flatFee =
      Number.isFinite(Number(plan?.flatFee)) ?
        Number(plan.flatFee) :
        Number(plan?.pricing?.[interval]?.flatFee ?? 0);

    if (!Number.isFinite(perSeat)) return null;
    if (!Number.isFinite(flatFee)) return null;

    return (perSeat * currentSeatCount) + flatFee;
  }, [
    isContact,
    plan?.perSeat,
    plan?.flatFee,
    plan?.pricing,
    interval,
    currentSeatCount,
  ]);

  const ctaState = useMemo(() => {
    const next = getPlanCtaState({
      plan,
      planKey,
      planPriceId: resolvedPriceId,
      currentSubscription,
      isPublic,
      isContactSalesPlan: isContact,
      isSeatCountUpdated,
      planConfigs,
    });

    console.log("[PlanCard2] CTA state resolved:", {
      planKey,
      currentSubscriptionStatus: currentSubscription?.status || null,
      currentSubscriptionInterval: currentSubscription?.interval || null,
      currentSubscriptionPriceId: currentSubscription?.priceId || null,
      targetPlanPriceId: resolvedPriceId,
      targetPlanInterval: interval,
      currentSubscriptionPlanName: currentSubscription?.planName || null,
      currentSubscriptionPlanKey: currentSubscription?.planKey || null,
      ctaState: next,
    });

    return next;
  }, [
    plan,
    planKey,
    resolvedPriceId,
    currentSubscription,
    isPublic,
    isContact,
    isSeatCountUpdated,
    interval,
  ]);

  const buttonText = ctaState.label;
  const isFeaturedPlan = plan?.featured === true;

  /* ----------------------------------------------------------------
   * Diagnostics
   * ---------------------------------------------------------------- 
  useEffect(() => {
    console.log("[PlanCard2] Mounted:", {
      planKey,
      interval,
      context,
      isContact,
      isFeaturedPlan,
      resolvedPriceId,
    });
  }, []); // mount only

  useEffect(() => {
    console.log("[PlanCard2] Render state update:", {
      planKey,
      planName: plan?.name || null,
      interval,
      displayedSeatPrice,
      priceMetaText,
      isContact,
      isFeaturedPlan,
      resolvedPriceId,
      currentSubscriptionPriceId,
      isCurrentPlan,
      isEffectiveCurrent,
      isTrialing,
      isRestart,
      currentSeatCount,
      isSeatCountUpdated,
      totalPrice,
      ctaLabel: ctaState?.label || null,
      ctaDisabled: ctaState?.disabled ?? null,
      ctaReason: ctaState?.reason || null,
      featureCount: Array.isArray(plan?.features) ? plan.features.length : 0,
      tooltipBackground: "#111827",
      inkColor: "var(--utm-ink, #0F1A2A)",
    });
  }, [
    planKey,
    plan?.name,
    interval,
    displayedSeatPrice,
    priceMetaText,
    isContact,
    isFeaturedPlan,
    resolvedPriceId,
    currentSubscriptionPriceId,
    isCurrentPlan,
    isEffectiveCurrent,
    isTrialing,
    isRestart,
    currentSeatCount,
    isSeatCountUpdated,
    totalPrice,
    ctaState,
    plan?.features,
  ]);

*/

  /* ----------------------------------------------------------------
   * CTA behavior
   * ---------------------------------------------------------------- */
  const handlePlanAction = useCallback(() => {
    console.log("[PlanCard2] handlePlanAction:", {
      planKey,
      planName: plan?.name || null,
      interval,
      isContact,
      resolvedPriceId,
      normalizedPlanPriceId: plan?.priceId || null,
      normalizedPlanFlatFee: plan?.flatFee ?? null,
      normalizedPlanPerSeat: plan?.perSeat ?? null,
      normalizedDisplayedSeatPrice: plan?.displayedSeatPrice ?? null,
    });

    if (isContact) {
      const contactUrl = plan?.cta?.url || plan?.cta?.contact?.url || null;

      if (contactUrl) {
        window.location.href = contactUrl;
      } else {
        console.warn("[PlanCard2] Contact-sales CTA has no URL:", {
          planKey,
          plan,
        });
      }
      return;
    }

    if (typeof onPlanSelect !== "function") {
      console.warn("[PlanCard2] onPlanSelect missing for self-serve plan:", {
        planKey,
        plan,
      });
      return;
    }

    onPlanSelect({
      ...plan,
      key: planKey,
      interval,
      priceId: resolvedPriceId,
      displayedSeatPrice:
        plan?.displayedSeatPrice != null ?
          plan.displayedSeatPrice :
          displayedSeatPrice,
      perSeat:
        Number.isFinite(Number(plan?.perSeat)) ?
          Number(plan.perSeat) :
          (plan?.pricing?.[interval]?.perSeat ?? null),
      flatFee:
        Number.isFinite(Number(plan?.flatFee)) ?
          Number(plan.flatFee) :
          (plan?.pricing?.[interval]?.flatFee ?? 0),
    });
  }, [
    planKey,
    plan,
    interval,
    isContact,
    resolvedPriceId,
    displayedSeatPrice,
    onPlanSelect,
  ]);

  const isDisabled = ctaState.disabled;
  /* ----------------------------------------------------------------
   * Render
   * ---------------------------------------------------------------- */
  return (
    <Box
      sx={{
        background: "var(--utm-card, #FFFFFF)",
        border: isFeaturedPlan
          ? "1px solid rgba(41,49,137,.22)"
          : "1px solid rgba(15,26,42,.10)",
        borderRadius: "22px",
        padding: "28px 24px 24px",
        boxShadow: isFeaturedPlan
          ? "0 14px 32px rgba(41,49,137,.09)"
          : "0 10px 28px rgba(15,26,42,.06)",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "transform .12s ease, box-shadow .15s ease, border-color .15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 16px 36px rgba(15,26,42,.09)",
          borderColor: "rgba(15,26,42,.16)",
        },
      }}
      aria-label={`${plan?.name || "Plan"} plan`}
    >
      {/* ------------------------------------------------------------
       * Head
       * ---------------------------------------------------------- */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          margin: "0 0 20px",
        }}
      >
        <Typography
          component="h3"
          sx={{
            margin: "0 0 8px",
            color: "var(--utm-ink, #0F1A2A)",
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            fontSize: "32px",
            lineHeight: 1.18,
            textAlign: "center",
            textRendering: "optimizeLegibility",
          }}
        >
          {plan?.name || ""}
        </Typography>

        {!!plan?.subheader && (
          <Typography
            component="p"
            sx={{
              margin: "0 0 8px",
              color: "var(--utm-ink, #0F1A2A)",
              fontFamily:
                "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
              fontWeight: 400,
              fontSize: "16px",
              textAlign: "center",
              lineHeight: 1.45,
              maxWidth: "26ch",
              textRendering: "optimizeLegibility",
            }}
          >
            {plan.subheader}
          </Typography>
        )}

        {!!plan?.bestFor && (
          <Typography
            component="p"
            sx={{
              margin: 0,
              color: "#64748b",
              fontFamily:
                "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
              fontWeight: 400,
              fontSize: "13px",
              lineHeight: 1.35,
              textAlign: "center",
              textRendering: "optimizeLegibility",
            }}
          >
            {plan.bestFor}
          </Typography>
        )}
      </Box>

      {/* ------------------------------------------------------------
       * Price block
       * ---------------------------------------------------------- */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          margin: "0 0 18px",
        }}
      >
        <Box
          aria-label={isContact ? "Custom pricing" : "Per seat price"}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: "4px",
            marginBottom: "6px",
          }}
        >
          {isContact ? (
            <Typography
              component="span"
              sx={{
                fontSize: "clamp(52px,5.5vw,64px)",
                fontFamily:
                  "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                fontWeight: 700,
                color: "var(--utm-ink, #0F1A2A)",
                lineHeight: 0.92,
                letterSpacing: "-0.03em",
                whiteSpace: "nowrap",
                textRendering: "optimizeLegibility",
              }}
            >
              Custom
            </Typography>
          ) : (
            <>
              <Typography
                component="span"
                sx={{
                  fontSize: "28px",
                  fontFamily:
                    "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                  fontWeight: 400,
                  color: "var(--utm-ink, #0F1A2A)",
                  lineHeight: 1,
                  position: "relative",
                  top: "8px",
                }}
              >
                $
              </Typography>

              <Typography
                component="span"
                sx={{
                  fontSize: "clamp(52px,5.5vw,64px)",
                  fontFamily:
                    "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                  fontWeight: 700,
                  color: "var(--utm-ink, #0F1A2A)",
                  lineHeight: 0.92,
                  letterSpacing: "-0.03em",
                  whiteSpace: "nowrap",
                  textRendering: "optimizeLegibility",
                }}
              >
                {major}
              </Typography>

              <Typography
                component="span"
                sx={{
                  fontSize: "16px",
                  fontFamily:
                    "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                  fontWeight: 500,
                  color: "var(--utm-ink, #0F1A2A)",
                  lineHeight: 1,
                  position: "relative",
                  top: "10px",
                }}
              >
                .{minor}
              </Typography>
            </>
          )}
        </Box>

        <Typography
          component="p"
          sx={{
            margin: 0,
            color: "var(--utm-ink, #0F1A2A)",
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
            fontSize: "13px",
            fontWeight: 400,
            lineHeight: 1.35,
            textAlign: "center",
          }}
        >
          {priceMetaText}
        </Typography>
      </Box>

      {/* ------------------------------------------------------------
       * Total / billing cadence
       *
       * Hierarchy:
       * - Line 1: "Total $165 / month" or "Total $1650 / year"
       *   • same size as the "user / month" support line
       *   • same text color as the feature-intro caption
       *
       * - Line 2: "Billed monthly" or "Billed annually"
       *   • same text color as card feature text
       *   • slightly smaller than the total line
       *
       * Contact-sales plans intentionally hide this block.
       * ---------------------------------------------------------- */}
      <Box
        sx={{
          marginTop: "2px",
          marginBottom: "20px",
          textAlign: "left",
          minHeight: "32px", // reserve the same two-line space on every card
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        {!isContact && totalPrice !== null ? (
          <>
            <Typography
              component="p"
              sx={{
                margin: 0,
                color: "#475569",
                fontFamily:
                  "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                fontSize: "13px",
                fontWeight: 400,
                lineHeight: "1rem",
                textAlign: "left",
              }}
            >
              Total ${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(totalPrice)} / {interval === "annual" ? "year" : "month"}
            </Typography>

            <Typography
              component="p"
              sx={{
                margin: 0,
                marginTop: "2px",
                color: "#374151",
                fontFamily:
                  "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: 1.4,
                textAlign: "left",
              }}
            >
              Billed {interval === "annual" ? "annually" : "monthly"}
            </Typography>
          </>
        ) : (
          <>
            <Typography
              component="p"
              aria-hidden="true"
              sx={{
                margin: 0,
                fontSize: "13px",
                lineHeight: "1rem",
                color: "transparent",
                userSelect: "none",
              }}
            >
              Placeholder
            </Typography>

            <Typography
              component="p"
              aria-hidden="true"
              sx={{
                margin: 0,
                marginTop: "2px",
                fontSize: "12px",
                lineHeight: 1.4,
                color: "transparent",
                userSelect: "none",
              }}
            >
              Placeholder
            </Typography>
          </>
        )}
      </Box>

      {/* ------------------------------------------------------------
       * CTA
       * ---------------------------------------------------------- */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginTop: 0,
          marginBottom: "20px",
        }}
      >
        <Button
          fullWidth={false}
          onClick={handlePlanAction}
          disabled={isDisabled}
          sx={{
            width: "70%",
            minHeight: "46px",
            justifyContent: "center",
            borderRadius: "14px",
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
            fontWeight: 700,
            fontSize: "16px",
            textTransform: "none",
            boxShadow: isFeaturedPlan
              ? "0 8px 18px rgba(72,100,255,.16)"
              : "none",
            border: "1.5px solid var(--utm-cta-blue, #4864ff)",
            background: isFeaturedPlan ? "var(--utm-cta-blue, #4864ff)" : "#FFFFFF",
            color: isFeaturedPlan ? "#FFFFFF" : "var(--utm-cta-blue, #4864ff)",
            "&:hover": {
              background: isFeaturedPlan
                ? "var(--utm-cta-blue-hover, #3648d4)"
                : "rgba(72,100,255,.06)",
              borderColor: isFeaturedPlan
                ? "var(--utm-cta-blue-hover, #3648d4)"
                : "var(--utm-cta-blue, #4864ff)",
              boxShadow: isFeaturedPlan
                ? "0 10px 22px rgba(72,100,255,.20)"
                : "none",
            },
            "&.Mui-disabled": {
              background: "#e0e0e0",
              color: "#9e9e9e",
              borderColor: "#e0e0e0",
            },
          }}
        >
          {buttonText}
        </Button>
      </Box>

      {/* ------------------------------------------------------------
       * Feature intro
       * ---------------------------------------------------------- */}
      {!!plan?.featureIntro && (
        <Typography
          component="p"
          sx={{
            margin: "0 0 14px",
            color: "#475569",
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: 1.45,
            textAlign: "left",
          }}
        >
          {plan.featureIntro}
        </Typography>
      )}

      {/* ------------------------------------------------------------
       * Features
       * ---------------------------------------------------------- */}
      <Box
        component="ul"
        aria-label="Plan highlights"
        sx={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        {(Array.isArray(plan?.features) ? plan.features : []).map((feature, index) => {
          const featureId =
            feature?.id ||
            feature?.name ||
            `${planKey || "plan"}-feature-${index}`;
          const featureName = feature?.name || "";
          const featureTooltip = feature?.tooltip || "";

          return (
            <Box
              component="li"
              key={featureId}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  flex: "0 0 auto",
                  width: 18,
                  height: 18,
                  marginTop: "1px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#16a34a",
                }}
              >
                <CheckIcon />
              </Box>

              <Box
                sx={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                {featureTooltip ? (
                  <Tooltip
                    title={featureTooltip}
                    arrow
                    placement="top-start"
                    slotProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: "#111827",
                          color: "#FFFFFF",
                          fontFamily:
                            "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                          fontSize: "12px",
                          fontWeight: 400,
                          lineHeight: 1.45,
                          padding: "10px 12px",
                          borderRadius: "10px",
                          boxShadow: "0 12px 28px rgba(15,26,42,.18)",
                          maxWidth: "320px",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "#111827",
                        },
                      },
                    }}
                  >
                    <Typography
                      component="span"
                      tabIndex={0}
                      sx={{
                        color: "#64748b",
                        fontFamily:
                          "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                        fontSize: "13px",
                        fontWeight: 400,
                        lineHeight: 1.4,
                        textDecorationLine: "underline",
                        textDecorationStyle: "dotted",
                        textDecorationColor: "rgba(100,116,139,.9)",
                        textUnderlineOffset: "3px",
                        cursor: "help",
                        outline: "none",
                        "&:hover": {
                          color: "var(--utm-ink, #0F1A2A)",
                          textDecorationColor: "rgba(15,26,42,.55)",
                        },
                        "&:focus-visible": {
                          color: "var(--utm-ink, #0F1A2A)",
                          textDecorationColor: "rgba(15,26,42,.55)",
                        },
                      }}
                    >
                      {featureName}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography
                    component="span"
                    sx={{
                      color: "#374151",
                      fontFamily:
                        "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                      fontSize: "13px",
                      fontWeight: 400,
                      lineHeight: 1.4,
                    }}
                  >
                    {featureName}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

PlanCard2.propTypes = {
  plan: PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    kind: PropTypes.string,
    featured: PropTypes.bool,
    pricingDisplay: PropTypes.string,
    availability: PropTypes.shape({
      selfServe: PropTypes.bool,
    }),
    cta: PropTypes.shape({
      type: PropTypes.string,
      label: PropTypes.string,
      url: PropTypes.string,
      contact: PropTypes.shape({
        method: PropTypes.string,
        url: PropTypes.string,
      }),
    }),
    subheader: PropTypes.string,
    bestFor: PropTypes.string,
    featureIntro: PropTypes.string,
    features: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string.isRequired,
        tooltip: PropTypes.string,
      }),
    ),
    priceId: PropTypes.string,
    flatFee: PropTypes.number,
    perSeat: PropTypes.number,
    displayedSeatPrice: PropTypes.number,
    pricing: PropTypes.shape({
      monthly: PropTypes.shape({
        flatFee: PropTypes.number,
        perSeat: PropTypes.number,
        priceId: PropTypes.string,
      }),
      annual: PropTypes.shape({
        flatFee: PropTypes.number,
        perSeat: PropTypes.number,
        priceId: PropTypes.string,
      }),
    }),
  }).isRequired,
  seatCount: PropTypes.number,
  context: PropTypes.oneOf(["public", "account"]).isRequired,
  interval: PropTypes.oneOf(["monthly", "annual"]).isRequired,
  onPlanSelect: PropTypes.func,
  currentSubscription: PropTypes.shape({
    priceId: PropTypes.string,
    status: PropTypes.string,
    seats: PropTypes.number,
  }),
};

export default React.memo(PlanCard2);